// ============================================================
// DM-LMS — Drawer hooks
// State and close triggers for the mobile nav drawer in AppShell.
// Kept separate from the component so each trigger is one small,
// independently testable hook.
// ============================================================

import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

interface DrawerState {
  isOpen: boolean;
  open:   () => void;
  close:  () => void;
  toggle: () => void;
}

/** Owns the drawer's open flag and exposes stable open, close and toggle handlers. */
export function useDrawerState(): DrawerState {
  const [isOpen, setIsOpen] = useState(false);
  const open   = useCallback(() => { setIsOpen(true); }, []);
  const close  = useCallback(() => { setIsOpen(false); }, []);
  const toggle = useCallback(() => { setIsOpen((current) => !current); }, []);
  return { isOpen, open, close, toggle };
}

/** Calls `close` when Escape is pressed, but only while the drawer is open. */
export function useCloseOnEscape(isOpen: boolean, close: () => void): void {
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => { document.removeEventListener("keydown", handleKeyDown); };
  }, [isOpen, close]);
}

/** Calls `close` whenever the pathname changes (also once on mount, which is a no-op). */
export function useCloseOnRouteChange(close: () => void): void {
  const { pathname } = useLocation();
  useEffect(() => {
    close();
  }, [pathname, close]);
}
